import { useEffect, useRef, useState } from 'react';

import { Table } from '@zillow/constellation-10';

import PropertyRow from '../PropertyRow';

import { useSelector } from '../../../selectors/useSelector';

import PropertyConstants from '../../../constants/PropertyConstants';
import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';

import type { WebAnalyticsLeaseDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { PropertyProps } from '../../../model/Property';
import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';

const { PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const PropertiesViewList = ({ listings }: { listings: Array<PropertyProps> }) => {
  const { user } = useSelector((state) => ({
    user: state.user,
  }));

  const tableRef = useRef<HTMLDivElement>(null);

  const [showMoreListingAlias, setShowMoreListingAlias] = useState('');
  const [hoveredCols, setHoveredCols] = useState<Record<string, boolean>>({});

  useEffect(() => {
    document.addEventListener('mouseup', closeOpenMenus);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeOpenMenus(e);
      }
    });

    return () => {
      document.removeEventListener('mouseup', closeOpenMenus);
      document.removeEventListener('keydown', closeOpenMenus);
    };
  }, [showMoreListingAlias]);

  const handleUpsellCellHover = (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => {
    // only fire the cs event if we have not already fired one for an cell in this column
    if (!hoveredCols[columnLabel]) {
      PropertiesPageCSEvents.PropertyCardHover({
        user,
        currentProperty,
        lease,
        triggerObject:
          PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS[
            columnLabel as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS
          ],
      });
      setHoveredCols((prev) => ({ ...prev, [columnLabel]: true }));
    }
  };

  const closeOpenMenus = (e: MouseEvent | KeyboardEvent) => {
    if (tableRef.current && showMoreListingAlias && !tableRef.current.contains(e.target as Node)) {
      setTimeout(() => {
        setShowMoreListingAlias('');
      }, 0);
    }
  };

  return (
    <Table.Responsive
      ref={tableRef}
      css={{ borderTop: '1px solid {colors.border.muted}', borderRadius: '0 0 {spacing.200} {spacing.200}' }}
    >
      <Table.Root
        appearance="horizontal"
        aria-label="Properties list"
        size="md"
        data-testid="PropertiesViewList"
        css={{ tableLayout: 'fixed' }}
      >
        <Table.Body>
          {listings.map((listing, index) => (
            <PropertyRow
              key={listing.listingAlias}
              index={index}
              listing={listing}
              showMoreListingAlias={showMoreListingAlias}
              setShowMoreListingAlias={setShowMoreListingAlias}
              onUpsellCellHover={handleUpsellCellHover}
            />
          ))}
        </Table.Body>
      </Table.Root>
    </Table.Responsive>
  );
};

export { PropertiesViewList };
export default PropertiesViewList;
